import { MutableRefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatService } from 'src/components/Chat/Chat.service';
import { emitLeadActiveChanged } from 'src/components/Chat/leadActiveChanged.event';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { firstName, formatMoney } from './LeadClose.functions';
import type { tRegisterAnswer } from './LeadClose.types';
import { useLeadClose } from './useLeadClose.hook';

/** A frame within this window after our own change is its echo, not news. */
export const OWN_ECHO_MS = 5000;

/** The toast's second line after a create (Appendix A). */
function savedMessage(
  answer: tRegisterAnswer,
  leadName: string,
): { key: string; values: Record<string, string> } | null {
  const name = firstName(leadName);
  if (answer.result.type === 'sold') {
    const amount = formatMoney(
      answer.result.amount ?? '0',
      answer.result.currency,
    );
    // A sale that replaced an old amount added nothing: say what Ganancia is now.
    return answer.result.prior_handling === 'replaced'
      ? { key: 'leadClose.toast.soldReplaced', values: { name, amount } }
      : { key: 'leadClose.toast.soldAdded', values: { name, amount } };
  }
  if (
    answer.archive_status === 'archived' ||
    answer.archive_status === 'already_archived'
  ) {
    return { key: 'leadClose.toast.archived', values: { name } };
  }
  return null;
}

/**
 * What follows a save, in the tab that saved, once (U14): the toast, the
 * archive retry, the analytics event. And what other tabs did: a lead frame
 * with a newer `close_changed_at` re-reads that lead's summary; a deleted lead
 * closes its open form. Renders nothing.
 */
export default function LeadCloseEffects(props: {
  ownChanges: MutableRefObject<Record<string, number>>;
}): null {
  const { ownChanges } = props;
  const { t } = useTranslation('chat/chat');
  const { addAlert } = useToastContext();
  const { trackEvent } = useAnalytics();
  const { currentWorkspace } = useWorkspaceContext();
  const { leadUpdatedEvent, leadDeletedEvent } = useSocketsContext();
  const { lastSave, summaryOf, refreshSummary, form, closeForm } =
    useLeadClose();
  const announced = useRef<string | null>(null);
  const changedAt = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!lastSave || announced.current === lastSave.id) return;
    announced.current = lastSave.id;
    if (lastSave.kind === 'edit') {
      if (lastSave.surface !== 'board')
        addAlert({
          type: eAlertType.SUCCESS,
          title: t('leadClose.toast.edited'),
          message: '',
        });
      return;
    }
    const { answer, leadId, leadName, source, surface } = lastSave;
    trackEvent('front.chat.lead_close.register.success', {
      displayName: 'Registrar resultado del lead',
      description: 'Cuántos resultados se registran y desde dónde',
      type: answer.result.type,
      source,
      archiveStatus: answer.archive_status,
    });
    if (answer.archive_status === 'archived')
      emitLeadActiveChanged({ id: leadId, active: false });
    // A save made from the board is announced on the board (U22).
    if (surface === 'board') return;
    const workspaceId = currentWorkspace?.id ?? '';
    if (answer.archive_status === 'failed') {
      addAlert({
        type: eAlertType.WARNING,
        title: t(`leadClose.toast.title.${answer.result.type}`),
        message: t(`leadClose.toast.archiveFailed.${answer.result.type}`),
        duration: 8000,
        action: {
          label: t('leadClose.toast.retry'),
          onClick: () => {
            // The archive of the bar's button, alone: the result is already saved.
            ChatService.archiveLead(workspaceId, leadId)
              .then(() => {
                emitLeadActiveChanged({ id: leadId, active: false });
                addAlert({
                  type: eAlertType.SUCCESS,
                  title: t('leadClose.toast.archivedTitle'),
                  message: '',
                });
              })
              .catch(() =>
                addAlert({
                  type: eAlertType.ERROR,
                  title: t('leadClose.toast.archiveFailedAgain'),
                  message: '',
                }),
              );
          },
        },
      });
      return;
    }
    const line = savedMessage(answer, leadName);
    addAlert({
      type: eAlertType.SUCCESS,
      title: t(`leadClose.toast.title.${answer.result.type}`),
      message: line ? t(line.key, line.values) : '',
    });
  }, [lastSave, addAlert, t, trackEvent, currentWorkspace?.id]);

  leadUpdatedEvent.useSubscription((event) => {
    const frame = event?.response;
    const stamp = frame?.close_changed_at;
    if (!frame?.uuid || !stamp) return;
    if ((changedAt.current[frame.uuid] ?? '') >= stamp) return;
    changedAt.current[frame.uuid] = stamp;
    // Only a lead some surface is showing; and not the echo of our own save.
    if (summaryOf(frame.uuid).status === 'idle') return;
    if (Date.now() - (ownChanges.current[frame.uuid] ?? 0) < OWN_ECHO_MS)
      return;
    refreshSummary(frame.uuid);
  });

  leadDeletedEvent.useSubscription((event) => {
    if (form && event?.response?.includes(form.lead.id)) {
      closeForm();
      addAlert({
        type: eAlertType.INFO,
        title: t('leadClose.toast.leadGone'),
        message: '',
      });
    }
  });

  return null;
}
